import { useEffect, useRef, useState } from "react"
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist"

function ensurePdfWorker(): void {
  if (!GlobalWorkerOptions.workerSrc) {
    GlobalWorkerOptions.workerSrc =
      "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.worker.mjs"
  }
}

type PartnerReportPdfPreviewProps = {
  blob: Blob
}

export function PartnerReportPdfPreview({ blob }: PartnerReportPdfPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState("")

  useEffect(() => {
    let cancelled = false
    const container = containerRef.current
    if (!container) return

    container.replaceChildren()
    setError("")
    ensurePdfWorker()

    const renderPdf = async () => {
      const bytes = await blob.arrayBuffer()
      const task = getDocument({ data: bytes })
      const pdf = await task.promise

      for (let pageNumber = 1; pageNumber <= pdf.numPages && !cancelled; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber)
        const initialViewport = page.getViewport({ scale: 1 })
        const availableWidth = Math.max(280, Math.min(container.clientWidth - 16, 900))
        const viewport = page.getViewport({ scale: availableWidth / initialViewport.width })
        const canvas = document.createElement("canvas")
        const context = canvas.getContext("2d")
        if (!context) throw new Error("PDF preview is not supported by this browser.")

        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.className = "mx-auto max-w-full rounded border bg-white shadow-sm"
        canvas.setAttribute("aria-label", `Partner report page ${pageNumber}`)
        container.appendChild(canvas)
        await page.render({ canvasContext: context, viewport, intent: "display" }).promise
      }
    }

    renderPdf().catch((reason: unknown) => {
      if (!cancelled) {
        container.replaceChildren()
        setError(reason instanceof Error ? reason.message : "Unable to preview the PDF.")
      }
    })

    return () => {
      cancelled = true
      container.replaceChildren()
    }
  }, [blob])

  if (error) {
    return <p role="alert" className="rounded-md border border-destructive/40 p-4 text-sm text-destructive">{error}</p>
  }

  return <div ref={containerRef} className="max-h-[60vh] space-y-3 overflow-auto rounded-md bg-muted/40 p-2" />
}

